import {Component, Input, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {EMPTY, Observable} from 'rxjs';
import {switchMap, takeUntil, tap} from 'rxjs/operators';
import {AccountRole, LoadingState} from 'common';
import {BaseComponent} from '../base.component';
import {DialogConfirmComponent} from '../dialogs/dialog-confirm';
import {ApiKeyAccessAdminDto} from '../../dtos/apiKeyAccessAdmin.dto';
import {ApiKeyAccessState} from '../../models/apiKeyAccessState';
import {ApiKeyAccessAdminService} from '../../services/api/api-key-access-admin.service';

/**
 * The api key access section of the account addons tab.
 *
 * Unlike every other addon in this tab, the state is read from the backend record and never from
 * account.metaFields: the meta carrier is writable by the account owner, so a meta derived grant
 * could show an approval that Kinvent never gave.
 *
 * Showing the actions to an ADMIN only is presentation, not authorisation. This application has no
 * role check after login, so the refusal path is implemented and tested for every role.
 */
/** One dated entry in the access history. `actor` is absent when unknown or withheld. */
interface AccessHistoryEvent {
    label: string;
    date: Date;
    actor: string;
}

@Component({
    selector: 'app-account-api-key-access',
    templateUrl: './account-api-key-access.component.html',
    styleUrls: ['./account-api-key-access.component.scss']
})
export class AccountApiKeyAccessComponent extends BaseComponent implements OnInit {
    /**
     * The backend refuses an operator who may not decide access with this code.
     * It is matched before any http status, because 401 already means 'session expired' elsewhere
     * in this application and a refusal must never be reported as a logout.
     */
    private static readonly REFUSED_ERROR_CODE = 'ERR_1003';

    @Input()
    public accountCode: string;

    public access: ApiKeyAccessAdminDto;
    public loading: LoadingState;
    public actionInProgress: boolean;
    /**
     * How many keys the backend reported it actually stopped, set only after a withdrawal made in
     * this session. It may honestly differ from the count shown before the operator confirmed.
     */
    public lastRevokedKeyCount: number;
    public readonly states: typeof ApiKeyAccessState = ApiKeyAccessState;

    constructor(private apiKeyAccessAdminService: ApiKeyAccessAdminService,
                private dialog: MatDialog) {
        super();
        this.actionInProgress = false;
    }

    /**
     * Whether the operator is offered the grant and withdraw actions. Presentation only.
     */
    public get canDecide(): boolean {
        const role = this.userAccount?.accountRoleType;
        return role === AccountRole.ADMIN || role === AccountRole.MANAGER;
    }

    /**
     * Whether the account on screen is the operator's own. account-service refuses a grant there,
     * so "Give access" is not offered. Presentation only.
     */
    public get isOwnAccount(): boolean {
        return this.userAccount?.code === this.accountCode;
    }

    /**
     * Whether the operator is shown WHO approved or revoked. Presentation only, and deliberately a
     * separate question from canDecide: account-service guards the operations (ADMIN or MANAGER) and
     * the two actor emails (ADMIN only) with two different checks, because staff personal data is a
     * stricter thing than the state and the count. A MANAGER's read succeeds with both emails null,
     * so without this the card would tell them "No longer available" -- which says the person has
     * gone when the truth is that they may not see it. A MANAGER may decide but not see the actor,
     * so do not collapse the two.
     */
    public get canSeeActor(): boolean {
        return this.userAccount?.accountRoleType === AccountRole.ADMIN;
    }

    /**
     * How many dated events the history holds.
     *
     * The history is built from the record and from nothing else, so an event is listed when it has a
     * date and is absent otherwise -- which is what makes it safe to show no icons there: nothing in
     * it can be contradicted by the state the account is in now. Zero means the panel is not rendered
     * at all, because an empty expander promises something to open when there is nothing.
     */
    public get historyEventCount(): number {
        return this.historyEvents.length;
    }

    /**
     * The dated events, NEWEST FIRST -- ordered by the timestamp each one carries, never by the
     * order the fields happen to sit in on the record.
     *
     * That distinction is load bearing. The record holds ONE timestamp per event type, not an event
     * log, so a revoke -> re-request -> re-approve cycle leaves withdrawnOn behind from the earlier
     * cycle, OLDER than the request that followed it. Listing the fields in their declared order
     * then puts a stale revocation last, where a reader takes it for the most recent thing that
     * happened. Sorting by the actual timestamp sinks it to where it chronologically belongs.
     *
     * What this cannot do is show an event twice: a second revocation overwrites the first. See the
     * finding in the PRD -- a true audit trail needs a change in kinvent-cloud, not here.
     */
    public get historyEvents(): AccessHistoryEvent[] {
        const events: AccessHistoryEvent[] = [];

        if (this.access?.requestedOnDate !== undefined) {
            events.push({label: 'Requested by the customer', date: this.access.requestedOnDate, actor: undefined});
        }
        if (this.access?.grantedOnDate !== undefined) {
            events.push({
                label: 'Approved by KINVENT',
                date: this.access.grantedOnDate,
                actor: this.canSeeActor ? this.actorDisplay(this.access.grantedByEmail) : undefined
            });
        }
        if (this.access?.withdrawnOnDate !== undefined) {
            events.push({
                label: 'Revoked by KINVENT',
                date: this.access.withdrawnOnDate,
                actor: this.canSeeActor ? this.actorDisplay(this.access.withdrawnByEmail) : undefined
            });
        }

        return events.sort((a, b) => b.date.getTime() - a.date.getTime());
    }

    /** The expander's summary, so a single event never reads as '1 events'. */
    public get historySummary(): string {
        const count = this.historyEventCount;
        return count === 1 ? '1 event' : `${count} events`;
    }

    public ngOnInit(): void {
        this.load();
    }

    /**
     * The only read. It is never retried silently: the error state offers the operator a retry.
     */
    public load(): void {
        this.loading = LoadingState.LOADING;
        this.apiKeyAccessAdminService.get(this.accountCode)
            .pipe(takeUntil(this.destroySubject))
            .subscribe(access => {
                this.apply(access);
                this.loading = LoadingState.LOADED;
            }, () => {
                this.loading = LoadingState.ERROR;
            });
    }

    public onGrantClick(): void {
        this.actionInProgress = true;
        this.apiKeyAccessAdminService.grant(this.accountCode)
            .pipe(takeUntil(this.destroySubject))
            .subscribe(access => {
                this.actionInProgress = false;
                this.apply(access);
            }, error => {
                this.actionInProgress = false;
                this.notifyActionFailed(error, 'give', 'provide');
            });
    }

    /**
     * Re reads the record, then opens the confirmation on the count that read returned. Nothing is
     * sent until the operator confirms.
     *
     * The re read is the point of this method: a console tab is left open for hours, so the count
     * loaded by ngOnInit can understate how many live integrations an irreversible action is about
     * to stop. If the re read fails the dialog is not opened at all, because falling back to the
     * count on screen would announce exactly the number that cannot be trusted.
     *
     * The state that read returns also decides whether the confirmation opens at all. A record that
     * is no longer GRANTED offers no action, so a dialog over one is that action offered through a
     * race: its copy would state an effect the record cannot have, and it would contradict the card
     * the same response has just refreshed.
     *
     * How many keys the backend actually stopped is still reported afterwards, and the two may
     * honestly differ.
     */
    public onWithdrawClick(): void {
        if (this.actionInProgress) {
            return;
        }
        this.actionInProgress = true;
        this.apiKeyAccessAdminService.get(this.accountCode)
            .pipe(
                tap(access => {
                    this.actionInProgress = false;
                    this.apply(access);
                }),
                switchMap(access => {
                    if (access.stateType !== ApiKeyAccessState.GRANTED) {
                        this.notification.info('The API key access state changed. Nothing was revoked.');
                        return EMPTY;
                    }
                    return this.openWithdrawConfirmation(access.activeKeyCount);
                }),
                takeUntil(this.destroySubject)
            )
            .subscribe(confirmed => {
                if (!confirmed) {
                    return;
                }
                this.confirmWithdraw();
            }, () => {
                this.actionInProgress = false;
                this.notification.info('Could not start the revocation. Nothing was changed.');
            });
    }

    /**
     * The alert line. Three cases on purpose: "0 active API keys" reads as a mistake at the one
     * moment the operator most needs to trust the number, and "1 active API keys" is simply wrong.
     * Zero is a real outcome, not an edge case -- it is the ONLY outcome when the account holds no
     * keys, and the action still revokes access.
     */
    private stopWarning(activeKeyCount: number): string {
        if (activeKeyCount === 0) {
            return 'No API keys are active, so nothing stops working. Access is still revoked.';
        }
        if (activeKeyCount === 1) {
            return '1 active API key stops working immediately.';
        }
        return `${activeKeyCount} active API keys stop working immediately.`;
    }

    /** The second half of "What this does", which has nothing to say when there is nothing to stop. */
    private breakSentence(activeKeyCount: number): string {
        if (activeKeyCount === 0) {
            return 'No key is active right now, so no integration is affected.';
        }
        if (activeKeyCount === 1) {
            return 'The integration using the active key breaks at once.';
        }
        return 'Every integration using these keys breaks at once.';
    }

    /**
     * The count named here is the one the re read returned, seconds before the operator reads it.
     * No as of qualifier is added on purpose: the number is current, and the number that matters
     * afterwards is revokedKeyCount, which the backend reports.
     */
    private openWithdrawConfirmation(activeKeyCount: number): Observable<boolean> {
        return this.dialog.open(DialogConfirmComponent, {
            data: {
                title: 'Revoke API key access for this account?',
                warning: this.stopWarning(activeKeyCount),
                message: 'What this does\n'
                    + 'Revokes API access for the whole account. '
                    + this.breakSentence(activeKeyCount) + '\n\n'
                    + 'What happens next\n'
                    + 'The account owner must request access again.\n\n'
                    + 'Who is told\n'
                    + 'The account owner is emailed. Integrators are not told: they have no identity in the product.',
                confirmText: 'Revoke access',
                cancelText: 'Cancel'
            }
        }).afterClosed();
    }

    public confirmWithdraw(): void {
        this.actionInProgress = true;
        this.apiKeyAccessAdminService.withdraw(this.accountCode)
            .pipe(takeUntil(this.destroySubject))
            .subscribe(access => {
                this.actionInProgress = false;
                this.lastRevokedKeyCount = access.revokedKeyCount;
                this.apply(access);
            }, error => {
                this.actionInProgress = false;
                this.notifyActionFailed(error, 'revoke', 'revoke');
            });
    }

    /**
     * The staff member who acted, as the backend resolved them. A missing email means the staff
     * account no longer exists, which is not the same as nobody having acted.
     */
    public actorDisplay(email: string): string {
        if (!email) {
            return 'No longer available';
        }
        return email;
    }

    /**
     * The only place this.access is assigned, so an error path can never leave a half applied state
     * on screen. What is shown is always what the backend last returned.
     */
    private apply(access: ApiKeyAccessAdminDto): void {
        this.access = access;
    }

    /**
     * Messages are static on purpose: an account code, an email or a url must never reach the
     * snackbar or the browser console, which is the only trace this application has.
     */
    private notifyActionFailed(error: any, action: string, refusedAction: string): void {
        if (error?.error?.errorCode === AccountApiKeyAccessComponent.REFUSED_ERROR_CODE) {
            this.notification.info(`You are not permitted to ${refusedAction} API key access.`);
            return;
        }
        this.notification.info(`Could not ${action} API key access. Nothing was changed.`);
    }
}
